/**
 * 图片空间入库切面：自动补全 source / size / width / height。
 * 显式传入的非 upload 来源与已有 size 不会被覆盖。
 */
import { getImageSpaceIngestContext } from './imageSpaceIngestContext';
import { normalizePositiveByteSize, resolveRecordByteSize } from './imageSpaceByteSize';

/** navId → 上传原图时的 source（与 imageSpaceSourceOptions 对齐，新模块默认可直接用 navId） */
const NAV_ID_UPLOAD_SOURCE = {
  imageSpace: 'imageSpace',
  spaceHub: 'imageSpace',
  spaceCollection: 'imageSpace',
  spaceFavorites: 'imageSpace',
  spaceTrash: 'imageSpace',
  goods: 'goods',
  podProductDesign: 'podProductDesign',
  titles: 'titles',
  videos: 'videos',
  imageCrop: 'imageCrop',
  imageEnhance: 'imageEnhance',
  smartCutout: 'smartCutout',
  stampExtract: 'stampExtract',
  imageFission: 'imageFission',
  patternFission: 'patternFission',
  img2img: 'img2img',
  aiOutpaint: 'aiOutpaint',
  aiTextToImage: 'aiTextToImage',
  aiCopywriting: 'aiCopywriting',
  modelTryOn: 'modelTryOn',
  modelHandheld: 'modelHandheld',
  modelPoseFission: 'modelPoseFission',
  modelUniversalWear: 'modelUniversalWear',
  infringementDetection: 'infringementDetection',
  ipeWorkflow: 'workflow',
  ipeWorkflowTasks: 'workflow',
  platformListing: 'workflow_platform_listing',
  sourcingHub: 'sourcing',
  systemMaterialLibrary: 'materialHub',
  agentChat: 'agent',
  imageTemplates: 'imageTemplates',
  listingTemplates: 'listingTemplates',
  tableTemplates: 'listingTemplates',
};

/** 功能结果图专用 source（存在则 ingestKind=result 时使用） */
const NAV_ID_RESULT_SOURCE = {
  imageCrop: 'cropDetail',
  goods: 'goods_composite',
  podProductDesign: 'podProductDesign_composite',
};

const KNOWN_RESULT_SUFFIX_SOURCES = new Set([
  'stampExtract_result',
  'imageEnhance_result',
  'smartCutout_result',
  'videos_result',
]);

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

export function shouldAutoFillSource(rawSource) {
  const text = pickFirstNonEmpty(rawSource);
  if (!text) return true;
  const lower = text.toLowerCase();
  return lower === 'upload' || lower === 'local' || lower === 'manual' || text === '本地上传';
}

export function resolveNavIdToUploadSource(navId) {
  const id = pickFirstNonEmpty(navId);
  if (!id) return null;
  if (NAV_ID_UPLOAD_SOURCE[id]) return NAV_ID_UPLOAD_SOURCE[id];
  return id;
}

const SKIP_RESULT_UPGRADE_SOURCES = new Set([
  'agent',
  'sourcing',
  'workflow',
  'workflow-batch',
  'materialHub',
  'systemMaterial',
  'library',
  'upload',
  'imageSpace',
]);

function resolveResultSourceFromBase(base) {
  if (!base) return null;
  if (NAV_ID_RESULT_SOURCE[base]) return NAV_ID_RESULT_SOURCE[base];
  const suffixed = `${base}_result`;
  if (KNOWN_RESULT_SUFFIX_SOURCES.has(suffixed)) return suffixed;
  if (base !== 'upload' && base !== 'workflow' && base !== 'sourcing' && base !== 'workflow-batch') {
    return suffixed;
  }
  return base;
}

/**
 * @param {{ explicitSource?: string, ingestKind?: 'upload'|'result' }} [options]
 */
export function resolveIngestSource(options = {}) {
  const ctx = getImageSpaceIngestContext();
  const ingestKind = options.ingestKind || ctx.ingestKind || 'upload';
  const explicit = pickFirstNonEmpty(options.explicitSource);
  if (explicit && !shouldAutoFillSource(explicit)) {
    return explicit;
  }

  const ctxSource = pickFirstNonEmpty(ctx.source);
  if (ctxSource && !shouldAutoFillSource(ctxSource)) {
    return ingestKind === 'result'
      ? resolveResultSourceFromBase(ctxSource)
      : ctxSource;
  }

  const navId = pickFirstNonEmpty(ctx.workflowStepNavId, ctx.navId);
  const base = resolveNavIdToUploadSource(navId);
  if (!base) return 'upload';
  return ingestKind === 'result' ? resolveResultSourceFromBase(base) : base;
}

function normalizeDimension(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ ingestKind?: 'upload'|'result', fileMeta?: Record<string, unknown> }} [options]
 */
export function enrichImageSpaceRecord(record, options = {}) {
  if (!record || typeof record !== 'object') return record;
  const fileMeta = options.fileMeta || {};
  const next = { ...record };

  const currentSource = pickFirstNonEmpty(next.source, next.sourceType);
  if (shouldAutoFillSource(currentSource)) {
    const source = resolveIngestSource({
      explicitSource: currentSource,
      ingestKind: options.ingestKind,
    });
    next.source = source;
    if (!next.sourceType) next.sourceType = source;
  } else if (options.ingestKind === 'result') {
    const base = pickFirstNonEmpty(next.source, next.sourceType);
    if (
      base
      && !SKIP_RESULT_UPGRADE_SOURCES.has(base)
      && !String(base).endsWith('_result')
      && base !== 'cropDetail'
      && base !== 'goods_composite'
      && base !== 'podProductDesign_composite'
    ) {
      const upgraded = resolveResultSourceFromBase(base);
      if (upgraded && upgraded !== base) {
        next.source = upgraded;
        next.sourceType = upgraded;
      }
    }
  }

  const width = normalizeDimension(next.width ?? fileMeta.width);
  const height = normalizeDimension(next.height ?? fileMeta.height);
  if (width != null && next.width == null) next.width = width;
  if (height != null && next.height == null) next.height = height;

  const size = resolveRecordByteSize(next, fileMeta);
  if (size != null && normalizePositiveByteSize(next.size) == null) {
    next.size = size;
  }

  return next;
}

export function enrichImageSpaceRecordsForIngest(records, options = {}) {
  return (Array.isArray(records) ? records : []).map((record) => (
    enrichImageSpaceRecord(record, options)
  ));
}
